import React, { useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  ArrowLeft, PencilSimple, Trash, FilePdf, Cube, MapPin, Percent, Images, FolderOpen,
} from "@phosphor-icons/react";
import api, { fileUrl } from "@/lib/api";
import { eur, price, fdate, num } from "@/lib/format";
import { placeImage } from "@/lib/placeImage";
import { PageHeader, StatusBadge, Field, DemoBadge } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import PropertyForm from "@/components/PropertyForm";
import PropertyFeatures from "@/components/PropertyFeatures";
import { MatchRow } from "@/components/MatchCard";
import PropertyMap from "@/components/PropertyMap";
import NearbyPoi from "@/components/NearbyPoi";
import Property3D from "@/components/Property3D";
import FileUpload from "@/components/FileUpload";
import SharePanel from "@/components/SharePanel";
import { generateBrochure } from "@/lib/brochure";
import { localizedField } from "@/lib/localized";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useTranslation } from 'react-i18next'

export default function PropertyDetail() {
  const { t, i18n } = useTranslation()
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [brochureType, setBrochureType] = useState("Public presentation");
  const [active, setActive] = useState(0);
  const [poiHighlight, setPoiHighlight] = useState(null);
  const locationMapRef = useRef(null);

  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: async () => (await api.get("/meta")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const { data: p, isLoading } = useQuery({ queryKey: ["property", id], queryFn: async () => (await api.get(`/properties/${id}`)).data });
  const { data: matches = [] } = useQuery({ queryKey: ["match-prop", id], queryFn: async () => (await api.get(`/match/property/${id}`)).data });
  const { data: propMatches } = useQuery({ queryKey: ["matching-prop", id], queryFn: async () => (await api.get(`/matching/property/${id}`)).data });
  const { data: locInfo } = useQuery({ queryKey: ["place-for", id], queryFn: async () => (await api.get(`/places/for-property/${id}`)).data });
  const { data: analysis } = useQuery({ queryKey: ["analysis", id], queryFn: async () => (await api.get(`/properties/${id}/analysis`)).data });

  const update = useMutation({
    mutationFn: async (payload) => (await api.put(`/properties/${id}`, payload)).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["property", id] }); setEdit(false);
      if (data?.pending_approval) toast.info(t("perm.visibilityRequested", "Visibility change sent for Admin approval. Other changes were saved."));
      else toast.success(t("propertyUpdated", "Property updated"));
    },
  });
  const remove = useMutation({
    mutationFn: async () => (await api.delete(`/properties/${id}`)).data,
    onSuccess: (data) => {
      if (data?.pending_approval) {
        toast.success(t("perm.deleteRequested", "Deletion request sent for Admin approval."));
      } else {
        toast.success(t("perm.propertyDeleted", "Property deleted"));
        navigate("/properties");
      }
    },
  });
  const isAdmin = user?.role === "admin";

  const removeModel = async (fid) => {
    try {
      const { data } = await api.delete(`/files/${fid}`);
      qc.invalidateQueries({ queryKey: ["property", id] });
      if (data?.pending_approval) toast.info(t("perm.deleteRequested", "Deletion request sent for Admin approval."));
      else toast.success(t('model3dRemoved', '3D model removed'));
    } catch {
      toast.error(t('actionFailed', 'Action failed'));
    }
  };

  const removeMedia = async (fid, kind) => {
    const confirmed = window.confirm(
      kind === "video"
        ? t('confirmDeleteVideo', 'Delete this video? The property will remain safe.')
        : t('confirmDeletePhoto', 'Delete this photo? The property will remain safe.')
    );
    if (!confirmed) return;
    try {
      const { data } = await api.delete(`/files/${fid}`);
      await qc.invalidateQueries({ queryKey: ["property", id] });
      if (data?.pending_approval) {
        toast.info(t("perm.deleteRequested", "Deletion request sent for Admin approval."));
      } else {
        toast.success(kind === "video"
          ? t('videoDeleted', 'Video deleted')
          : t('photoDeleted', 'Photo deleted'));
      }
    } catch {
      toast.error(t('actionFailed', 'Action failed'));
    }
  };

  if (isLoading || !p)
    return <div className="flex justify-center py-40"><div className="animate-spin h-8 w-8 border-2 border-[#0B2545] border-t-transparent rounded-full" /></div>;

  const media = p.media || [];
  const photos = [
    ...(p.cover_image ? [{ id: "cover", url: p.cover_image, direct: true }] : []),
    ...(p.gallery || []).map((u, i) => ({ id: `g${i}`, url: u, direct: true })),
    ...media.filter((m) => (m.content_type || "").startsWith("image") && m.category !== "3d").map((m) => ({ id: m.id, url: fileUrl(m.id), direct: false })),
  ];
  const isModel3d = (m) => m.category === "model3d" || /\.(glb|gltf)$/i.test(m.original_filename || "") || (m.content_type || "").includes("gltf");
  const model3d = media.find(isModel3d) || null;
  const modelUrl = model3d ? fileUrl(model3d.id) : null;
  const videos = media.filter((m) => m.category === "video" || (m.content_type || "").startsWith("video"));
  const renderings3d = media.filter((m) => m.category === "3d");
  const documents = media.filter((m) => !(m.content_type || "").startsWith("image") && !(m.content_type || "").startsWith("video") && m.category !== "video" && m.category !== "3d" && !isModel3d(m));

  const contentLanguage = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const localized = (field) => localizedField(p, field, contentLanguage);

  const translatedCategory = p.category === "Land"
    ? t("propertyCategoryLand", "Land")
    : p.category === "Development project"
      ? t("propertyCategoryDevelopmentProject", "Development project")
      : p.category === "Hotel"
        ? t("propertyCategoryHotel", "Hotel")
        : p.category;

  const translatedCondition = p.condition === "Development project – completion required"
    ? t("conditionDevelopmentCompletionRequired", "Development project – completion required")
    : p.condition === "Development project"
      ? t("conditionDevelopmentProject", "Development project")
      : (p.condition || "—");

  const brochureOptions = [
    ["Public presentation", "brochurePublicPresentation"],
    ["Confidential investor presentation", "brochureConfidentialInvestor"],
    ["Owner report", "brochureOwnerReport"],
    ["Property summary", "brochurePropertySummary"],
    ["Investment memorandum", "brochureInvestmentMemorandum"],
    ["Development proposal", "brochureDevelopmentProposal"],
  ];

  const doBrochure = async () => {
    const localizedProperty = {
      ...p,
      title: localized("title"),
      description: localized("description"),
      development_potential: localized("development_potential"),
      exit_strategy: localized("exit_strategy"),
      investment_strengths: localized("investment_strengths"),
      investment_risks: localized("investment_risks"),
      category: translatedCategory,
      city: p.city === "Setúbal" ? t("placeSetubal", "Setúbal") : p.city,
      district: p.district === "Setúbal" ? t("placeSetubal", "Setúbal") : p.district,
    };

    try {
      await generateBrochure(localizedProperty, {
        type: brochureType,
        includeFinancial: true,
        includeConfidential:
          brochureType.includes("investor") ||
          brochureType.includes("Confidential") ||
          brochureType.includes("memorandum"),
        consultant: settings || {},
        language: contentLanguage,
        logoUrl: "/panatlantik-logo.png",
        images: photos.slice(0, 4).map((photo) => photo.url),
      });
      toast.success(t("brochureGenerated", "Brochure generated"));
    } catch (error) {
      console.error(error);
      toast.error(t("brochureGenerationFailed", "Brochure generation failed"));
    }
  };

  return (
    <div>
      <button onClick={() => navigate("/properties")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B2545] mb-4" data-testid="back-btn">
        <ArrowLeft size={16} /> {t('backToProperties', 'Back to properties')}
      </button>

      <PageHeader
        title={localized("title")}
        subtitle={t('referenceCategoryCityDistrict', '{{reference}} · {{category}} · {{city}}, {{district}}', {
          reference: p.reference,
          category: translatedCategory,
          city: p.city === "Setúbal" ? t("placeSetubal", "Setúbal") : p.city,
          district: p.district === "Setúbal" ? t("placeSetubal", "Setúbal") : p.district
        })}
        actions={
          <>
            <Select value={brochureType} onValueChange={setBrochureType}>
              <SelectTrigger className="w-56 rounded-sm bg-white" data-testid="brochure-type"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                {brochureOptions.map(([value, key]) => (
                  <SelectItem key={value} value={value}>{t(key, value)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={doBrochure} data-testid="generate-brochure" className="rounded-sm bg-[#C5A059] hover:bg-[#b08d4a] text-white">
              <FilePdf size={16} className="mr-1.5" /> {t("pdf", "PDF")}
            </Button>
            <Button variant="outline" onClick={() => setEdit(true)} data-testid="edit-property" className="rounded-sm"><PencilSimple size={16} /></Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" data-testid="delete-property" className="rounded-sm text-red-600 hover:text-red-700" title={isAdmin ? t('deleteProperty', 'Delete property?') : t('perm.requestDeletion', 'Request deletion')}><Trash size={16} /></Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-white rounded-sm">
                <AlertDialogHeader>
                  <AlertDialogTitle>{isAdmin ? t('deleteProperty', 'Delete property?') : t('perm.requestDeletionQ', 'Request deletion?')}</AlertDialogTitle>
                  <AlertDialogDescription>{isAdmin ? t('thisActionCannotBeUndone', 'This action cannot be undone.') : t('perm.requestDeletionDesc', 'This will send a deletion request to an Admin for approval.')}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="rounded-sm">{t('cancel', 'Cancel')}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => remove.mutate()} className="rounded-sm bg-red-600 hover:bg-red-700" data-testid="confirm-delete">{isAdmin ? t('delete', 'Delete') : t('perm.requestDeletion', 'Request deletion')}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        }
      />

      <TooltipProvider delayDuration={150}>
        <div className="flex flex-wrap items-center gap-2 mb-6">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                data-testid="status-badge-link"
                onClick={() => navigate(`/properties?status=${encodeURIComponent(p.status)}`)}
                className="rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059] focus-visible:ring-offset-1 cursor-pointer hover:opacity-80 transition-opacity"
                aria-label={t('viewStatusProperties', 'View {{status}} properties', { status: p.status })}
              >
                <StatusBadge status={p.status} />
              </button>
            </TooltipTrigger>
            <TooltipContent>{t('viewStatusProperties', 'View {{status}} properties', { status: p.status })}</TooltipContent>
          </Tooltip>

          {p.is_demo && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} data-testid="sample-badge" className="rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059] focus-visible:ring-offset-1 cursor-help">
                  <DemoBadge />
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{t('sampleBadgeTooltip', 'Sample data included for demonstration. It does not affect your real portfolio.')}</TooltipContent>
            </Tooltip>
          )}

          {p.approval_status && p.approval_status !== "approved" && (
            <span data-testid="approval-status-badge" className={`text-xs px-2 py-0.5 rounded-sm uppercase tracking-wide ${p.approval_status === "rejected" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"}`}>
              {t(`perm.status.${p.approval_status}`, p.approval_status)}
            </span>
          )}

          {p.visibility === "confidential" && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} data-testid="confidential-badge" className="text-xs bg-[#0B2545] text-white px-2 py-0.5 rounded-sm uppercase tracking-wide cursor-help focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C5A059] focus-visible:ring-offset-1">
                  {t('confidential', 'Confidential')}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">{t('confidentialBadgeTooltip', 'Confidential listing — excluded from public presentations and only visible to your team.')}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </TooltipProvider>

      {/* Bento gallery */}
      {photos.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-8">
          <div className="md:col-span-8 h-[300px] md:h-[420px] rounded-sm overflow-hidden bg-slate-100">
            <img src={photos[active]?.url} alt={localized("title")} className="w-full h-full object-contain object-center bg-white" />
          </div>
          <div className="md:col-span-4 grid grid-cols-3 md:grid-cols-2 gap-3">
            {photos.slice(0, 4).map((ph, i) => (
              <button key={ph.id} onClick={() => setActive(i)} data-testid={`gallery-thumb-${i}`}
                className={`h-24 md:h-[100px] rounded-sm overflow-hidden border-2 ${active === i ? "border-[#C5A059]" : "border-transparent"}`}>
                <img src={ph.url} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="rounded-sm bg-slate-100 flex-wrap h-auto mb-6">
          <TabsTrigger value="overview" className="rounded-sm" data-testid="tab-overview">{t('overview', 'Overview')}</TabsTrigger>
          <TabsTrigger value="financial" className="rounded-sm" data-testid="tab-financial">{t('financial', 'Financial')}</TabsTrigger>
          <TabsTrigger value="analysis" className="rounded-sm" data-testid="tab-analysis">{t('analysis.title', 'Analysis')}</TabsTrigger>
          <TabsTrigger value="matching" className="rounded-sm" data-testid="tab-matching"><Percent size={14} className="mr-1" />{t('matching.title', 'Matching')}</TabsTrigger>
          <TabsTrigger value="media" className="rounded-sm" data-testid="tab-media"><Images size={14} className="mr-1" />{t('media', 'Media')}</TabsTrigger>
          <TabsTrigger value="3d" className="rounded-sm" data-testid="tab-3d"><Cube size={14} className="mr-1" />{t('3dProject', '3D Project')}</TabsTrigger>
          <TabsTrigger value="location" className="rounded-sm" data-testid="tab-location">{t('location', 'Location')}</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6">
              <h3 className="font-serif text-xl mb-4">{t('description', 'Description')}</h3>
              <p className="text-slate-600 leading-relaxed">{localized("description") || t('noDescriptionProvided', 'No description provided.')}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-5 mt-6 pt-6 border-t border-slate-100">
                <Field label={t("bedrooms", "Bedrooms")}>{p.bedrooms || "—"}</Field>
                <Field label={t("bathrooms", "Bathrooms")}>{p.bathrooms || "—"}</Field>
                <Field label={t('grossArea', 'Gross area')}>{p.gross_area ? t('valM', '{{val}} m²', { val: num(p.gross_area) }) : "—"}</Field>
                <Field label={t('landArea', 'Land area')}>{p.land_area ? t('valM', '{{val}} m²', { val: num(p.land_area) }) : "—"}</Field>
                <Field label={t("floors", "Floors")}>{p.floors || "—"}</Field>
                <Field label={t('yearBuilt', 'Year built')}>{p.year_built || "—"}</Field>
                <Field label={t("energy", "Energy")}>{p.energy_certificate || "—"}</Field>
                <Field label={t("condition", "Condition")}>{translatedCondition}</Field>
                <Field label={t('possibleUnits', 'Possible units')}>{p.possible_units || "—"}</Field>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
              <div className="text-center pb-4 border-b border-slate-100">
                <div className="text-xs uppercase tracking-wide text-slate-400">{t('askingPrice', 'Asking Price')}</div>
                <div className="font-serif text-3xl text-[#0B2545] mt-1">{price(p.asking_price)}</div>
              </div>
              <Field label={t('developmentPotential', 'Development potential')}>{localized("development_potential") || "—"}</Field>
              <Field label={t('exitStrategy', 'Exit strategy')}>{localized("exit_strategy") || "—"}</Field>
              <div className="flex flex-wrap gap-2 pt-2">
                {p.hotel_potential && <span className="text-xs bg-[#C5A059]/15 text-[#8a6d2f] px-2 py-0.5 rounded-sm">{t('hotelPotential', 'Hotel potential')}</span>}
                {p.student_residence_potential && <span className="text-xs bg-[#C5A059]/15 text-[#8a6d2f] px-2 py-0.5 rounded-sm">{t('studentResidence', 'Student residence')}</span>}
                {p.senior_residence_potential && <span className="text-xs bg-[#C5A059]/15 text-[#8a6d2f] px-2 py-0.5 rounded-sm">{t('seniorResidence', 'Senior residence')}</span>}
              </div>
            </div>
            {isAdmin && <div className="lg:col-span-3"><SharePanel recordType="property" recordId={id} /></div>}
          </div>
        </TabsContent>

        <TabsContent value="analysis">
          {!analysis ? <p className="text-slate-400 text-sm p-6">{t('analysis.noData', 'Not enough data for analysis')}</p> : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="analysis-panel">
              <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-serif text-xl">{t('analysis.title', 'Analysis')}</h3>
                  <div className="text-xs text-slate-500">{t('analysis.confidence', 'Confidence')}: <b className="text-[#0B2545]">{analysis.confidence}%</b></div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-5">
                  <Field label={t('analysis.pricePerM2', 'Price / m²')}>{analysis.metrics.price_per_m2 ? eur(analysis.metrics.price_per_m2) : '—'}</Field>
                  <Field label={t('analysis.medianComps', 'Median comparable €/m²')}>{analysis.metrics.median_comparable_ppm2 ? eur(analysis.metrics.median_comparable_ppm2) : '—'}</Field>
                  <Field label={t('analysis.grossYield', 'Gross yield')}>{analysis.metrics.gross_yield_pct != null ? `${analysis.metrics.gross_yield_pct}%` : '—'}</Field>
                  <Field label={t('analysis.capRate', 'Cap rate')}>{analysis.metrics.cap_rate_pct != null ? `${analysis.metrics.cap_rate_pct}%` : '—'}</Field>
                  <Field label={t('analysis.roi', 'ROI')}>{analysis.metrics.roi_pct != null ? `${analysis.metrics.roi_pct}%` : '—'}</Field>
                  <Field label={t('marketValue', 'Market value')}>{analysis.metrics.market_value ? eur(analysis.metrics.market_value) : '—'}</Field>
                </div>
                <h4 className="text-xs uppercase tracking-wide text-slate-400 mt-6 mb-3">{t('analysis.keyFactors', 'Key factors')}</h4>
                <div className="space-y-2">
                  {analysis.factors.map((f, i) => (
                    <div key={i} data-testid={`analysis-factor-${i}`} className="flex items-center justify-between text-sm border border-slate-100 rounded-sm px-3 py-2">
                      <span className="text-slate-600">{t('analysis.factor.' + f.code, f.code)}</span>
                      <span className={f.impact === 'positive' ? 'text-emerald-600 font-medium' : f.impact === 'negative' ? 'text-red-600 font-medium' : 'text-slate-500'}>{f.value}</span>
                    </div>
                  ))}
                  {analysis.factors.length === 0 && <p className="text-slate-400 text-sm">{t('analysis.noData', 'Not enough data for analysis')}</p>}
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-6">
                <h4 className="font-serif text-lg mb-3">{t('analysis.flags', 'Opportunities & risks')}</h4>
                <div className="space-y-2">
                  {analysis.flags.map((fl, i) => (
                    <div key={i} data-testid={`analysis-flag-${i}`} className={`rounded-sm px-3 py-2 text-sm border ${fl.type === 'opportunity' ? 'bg-emerald-50 border-emerald-100 text-emerald-800' : 'bg-red-50 border-red-100 text-red-800'}`}>
                      <div className="font-medium">{t('analysis.flag.' + fl.code + '.label', fl.code)}</div>
                      <div className="text-xs opacity-80">{t('analysis.flag.' + fl.code + '.detail', fl.params || {})}</div>
                    </div>
                  ))}
                  {analysis.flags.length === 0 && <p className="text-slate-400 text-sm">—</p>}
                </div>
                <div className="text-xs text-slate-400 mt-4">{analysis.metrics.comparable_count} {t('analysis.comparables', 'comparable properties')}</div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="financial">
          <div className="bg-white border border-slate-200 rounded-sm p-6 grid grid-cols-2 sm:grid-cols-3 gap-6">
            <Field label={t('askingPrice2', 'Asking price')}>{price(p.asking_price)}</Field>
            <Field label={t('minimumPrice', 'Minimum price')}>{eur(p.min_price)}</Field>
            <Field label={t('marketValue', 'Market value')}>{eur(p.market_value)}</Field>
            <Field label={t('commission', 'Commission %')}>{p.commission_pct ? `${p.commission_pct}%` : "—"}</Field>
            <Field label={t('estimatedCommission', 'Estimated commission')}>{p.asking_price && p.commission_pct ? eur(p.asking_price * p.commission_pct / 100) : "—"}</Field>
            <Field label={t('estimatedRoi', 'Estimated ROI')}>{p.estimated_roi ? `${p.estimated_roi}%` : "—"}</Field>
            <Field label={t('expectedAnnualIncome', 'Expected annual income')}>{eur(p.expected_annual_income)}</Field>
            <Field label={t('developmentCost', 'Development cost')}>{eur(p.estimated_development_cost)}</Field>
            <Field label={t('expectedOccupancy', 'Expected occupancy')}>{p.expected_occupancy ? `${p.expected_occupancy}%` : "—"}</Field>
            <div className="col-span-2 sm:col-span-3 pt-4 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-6">
              <Field label={t('investmentStrengths', 'Investment strengths')}>{localized("investment_strengths") || "—"}</Field>
              <Field label={t('investmentRisks', 'Investment risks')}>{localized("investment_risks") || "—"}</Field>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="matching">
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <h3 className="font-serif text-xl mb-1">{t('matching.propertyMatches', 'Matched Buyers, Investors & Requirements')}</h3>
            <p className="text-slate-500 text-sm mb-5">{t('matching.propertyMatchesSub', 'Ranked by fit using budget, type, location, yield, analysis & buyer quality.')}</p>
            <div className="space-y-2" data-testid="property-matches">
              {(propMatches?.results || []).filter((r) => r.match.score >= 40).map((r) => (
                <MatchRow key={r.source_key} name={r.party.name}
                  subtitle={`${t(`matching.party.${r.party.party_type}`, r.party.party_type)}${r.kind === 'requirement' ? ' · ' + t('navigation.requirements', 'Requirements') : ''}`}
                  match={r.match} testid={`prop-match-${r.party.id}`} />
              ))}
              {(!propMatches || (propMatches.results || []).filter((r) => r.match.score >= 40).length === 0) && <p className="text-slate-400 text-sm">{t('noBuyersOrInvestorsToMatchYet', 'No buyers or investors to match yet.')}</p>}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="media">
          <div className="space-y-6">
            <div>
              <h4 className="text-xs uppercase tracking-wide text-slate-400 mb-3">{t('uploadPhotos', 'Upload Photos')}</h4>
              <FileUpload propertyId={id} category="photo" accept="image/*" onUploaded={() => qc.invalidateQueries({ queryKey: ["property", id] })} />
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-wide text-slate-400 mb-3">{t('uploadVideo', 'Upload Video')}</h4>
              <FileUpload propertyId={id} category="video" accept="video/mp4,video/webm,video/quicktime" allowedExt={["mp4", "webm", "mov"]} label={t('dragVideo', 'Drag & drop or click to upload an MP4, WebM or MOV video')} onUploaded={() => qc.invalidateQueries({ queryKey: ["property", id] })} />
              <p className="text-xs text-slate-400 mt-2">{t('conceptVideoNotice', 'Concept videos must be clearly identified as conceptual visualisations and are subject to planning and architectural approval.')}</p>
            </div>
            {photos.filter((ph) => !ph.direct).length > 0 && (
              <div>
                <h4 className="text-xs uppercase tracking-wide text-slate-400 mb-3">{t('uploadedPhotos', 'Uploaded Photos')}</h4>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {photos.filter((ph) => !ph.direct).map((ph) => (
                    <div key={ph.id} className="relative group">
                      <img src={ph.url} alt="" className="h-32 w-full object-cover rounded-sm border border-slate-200" />
                      <button
                        type="button"
                        onClick={() => removeMedia(ph.id, "photo")}
                        className="absolute top-2 right-2 p-2 rounded-sm bg-white/95 text-red-600 shadow hover:bg-red-600 hover:text-white"
                        aria-label={t('deletePhoto', 'Delete photo')}
                        title={t('deletePhoto', 'Delete photo')}
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {videos.length > 0 && (
              <div>
                <h4 className="text-xs uppercase tracking-wide text-slate-400 mb-3">{t('uploadedVideos', 'Uploaded Videos')}</h4>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {videos.map((v) => (
                    <div key={v.id} className="relative border border-slate-200 rounded-sm overflow-hidden bg-black">
                      <video controls preload="metadata" className="w-full max-h-96" src={fileUrl(v.id)}>
                        Your browser does not support video playback.
                      </video>
                      <button
                        type="button"
                        onClick={() => removeMedia(v.id, "video")}
                        className="absolute top-2 right-2 p-2 rounded-sm bg-white/95 text-red-600 shadow hover:bg-red-600 hover:text-white"
                        aria-label={t('deleteVideo', 'Delete video')}
                        title={t('deleteVideo', 'Delete video')}
                      >
                        <Trash size={16} />
                      </button>
                      <div className="bg-white px-3 py-2 text-xs text-slate-600 truncate">{v.original_filename}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {documents.length > 0 && (
              <div>
                <h4 className="text-xs uppercase tracking-wide text-slate-400 mb-3">{t('documentsFiles', 'Documents & Files')}</h4>
                <div className="space-y-2">
                  {documents.map((d) => (
                    <a key={d.id} href={fileUrl(d.id)} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3 border border-slate-100 rounded-sm hover:bg-slate-50">
                      <FolderOpen size={20} className="text-[#C5A059]" />
                      <span className="text-sm text-slate-700 flex-1 truncate">{d.original_filename}</span>
                      <span className="text-xs text-slate-400 uppercase">{d.category}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="3d">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Property3D modelUrl={modelUrl} />
              <p className="text-xs text-slate-400 mt-2">{t('model3dCaption', 'Interactive, property-specific 3D model. Rotate, zoom and reset the view.')}</p>
            </div>
            <div className="space-y-6">
              <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-3">
                <h3 className="font-serif text-lg">{t('model3dGlbGltf', '3D Model (GLB / GLTF)')}</h3>
                {model3d ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Cube size={16} className="text-[#C5A059]" />
                      <span className="truncate" data-testid="model3d-filename">{model3d.original_filename}</span>
                    </div>
                    <Button variant="outline" size="sm" className="rounded-sm text-red-600 border-red-200" data-testid="model3d-remove" onClick={() => removeModel(model3d.id)}>{t('removeModel', 'Remove model')}</Button>
                    <div>
                      <div className="text-xs text-slate-400 mb-1">{t('replaceModel', 'Replace model')}</div>
                      <FileUpload propertyId={id} category="model3d" accept=".glb,.gltf" allowedExt={["glb", "gltf"]} label={t('uploadModel3d', 'Upload GLB / GLTF model')} onUploaded={() => qc.invalidateQueries({ queryKey: ["property", id] })} />
                    </div>
                  </div>
                ) : (
                  <FileUpload propertyId={id} category="model3d" accept=".glb,.gltf" allowedExt={["glb", "gltf"]} label={t('uploadModel3d', 'Upload GLB / GLTF model')} onUploaded={() => qc.invalidateQueries({ queryKey: ["property", id] })} />
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-3">
                <h3 className="font-serif text-lg">{t('externalViewerSketchupRevit', 'External 3D viewer (SketchUp / Revit)')}</h3>
                {p.external_3d_url ? (
                  <a href={p.external_3d_url} target="_blank" rel="noopener noreferrer" data-testid="external-3d-link">
                    <Button variant="outline" className="w-full rounded-sm"><Cube size={16} className="mr-1.5" />{t('openExternalViewer', 'Open external viewer')}</Button>
                  </a>
                ) : (
                  <p className="text-sm text-slate-400">{t('notLinked', 'Not linked')}</p>
                )}
              </div>

              <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-3">
                <h3 className="font-serif text-lg">{t('renderingsImages', '3D Renderings & Images')}</h3>
                <FileUpload propertyId={id} category="3d" accept="image/jpeg,image/png,image/webp" allowedExt={["jpg", "jpeg", "png", "webp"]} label={t('uploadRenderings3dFiles', 'Upload 3D rendering images')} onUploaded={() => qc.invalidateQueries({ queryKey: ["property", id] })} />
                {renderings3d.length > 0 && (
                  <div className="grid grid-cols-1 gap-3 pt-2">
                    {renderings3d.map((r) => (
                      <div key={r.id} className="relative group border border-slate-200 rounded-sm overflow-hidden">
                        <img src={fileUrl(r.id)} alt={r.original_filename || "3D rendering"} className="w-full h-44 object-cover" />
                        <button
                          type="button"
                          onClick={() => removeMedia(r.id, "photo")}
                          className="absolute top-2 right-2 p-2 rounded-sm bg-white/95 text-red-600 shadow hover:bg-red-600 hover:text-white"
                          aria-label={t('deleteRendering', 'Delete 3D rendering')}
                          title={t('deleteRendering', 'Delete 3D rendering')}
                        >
                          <Trash size={16} />
                        </button>
                        <div className="px-3 py-2 text-xs text-slate-600 truncate bg-white">{r.original_filename}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="location">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center gap-2 text-slate-600"><MapPin size={18} className="text-[#C5A059]" />{t('addressPostal_codeCityDistrictCountry', '{{address}} {{postal_code}} {{city}}, {{district}}, {{country}}', { address: p.address, postal_code: p.postal_code, city: p.city, district: p.district, country: p.country })}</div>
              {p.lat && p.lng ? <div ref={locationMapRef}><PropertyMap properties={[p]} height="360px" highlight={poiHighlight} /></div> : <p className="text-slate-400 text-sm">{t('noCoordinatesSetForThisProperty', 'No coordinates set for this property.')}</p>}
              {p.lat && p.lng && <div className="text-xs text-slate-400">{t('places.coordinates', 'Coordinates')}: {p.lat}, {p.lng}</div>}

              <PropertyFeatures property={p} />

              {p.lat && p.lng && (
                <div className="bg-white border border-slate-200 rounded-sm p-5">
                  <NearbyPoi lat={p.lat} lng={p.lng} onLocate={(it) => {
                    setPoiHighlight({ lat: it.lat, lng: it.lng, name: it.name });
                    if (locationMapRef.current) locationMapRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
                  }} />
                </div>
              )}

              {locInfo?.nearby?.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-sm p-5">
                  <h4 className="font-serif text-lg mb-3">{t('places.nearbyProperties', 'Other Panatlantik properties nearby')}</h4>
                  <div className="space-y-2">
                    {locInfo.nearby.map((n) => (
                      <div key={n.id} data-testid={`nearby-${n.id}`} onClick={() => navigate(`/properties/${n.id}`)}
                        className="flex items-center gap-3 p-2.5 border border-slate-100 rounded-sm hover:bg-slate-50 cursor-pointer">
                        <div className="w-12 h-12 rounded-sm overflow-hidden bg-slate-100 flex-shrink-0">
                          {n.cover_image ? <img src={n.cover_image} alt="" className="w-full h-full object-contain object-center bg-white" /> : <div className="w-full h-full flex items-center justify-center text-slate-300"><MapPin size={16} /></div>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-slate-800 truncate">{n.title}</div>
                          <div className="text-xs text-slate-500">{n.city} · {price(n.asking_price)}</div>
                        </div>
                        <div className="text-xs text-[#C5A059] font-medium">{t('places.distanceKm', '{{km}} km away', { km: n.distance_km })}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-4">
              {locInfo?.place && (
                <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                  <div className="h-36 bg-slate-100">
                    <img src={placeImage(locInfo.place)} alt={locInfo.place.name} className="w-full h-full object-contain object-center bg-white" />
                  </div>
                  <div className="p-5">
                    <div className="text-xs uppercase tracking-wide text-slate-400">{t('places.profile', 'Place Profile')}</div>
                    <h4 className="font-serif text-lg text-slate-900 mt-0.5">{locInfo.place.name}</h4>
                    <div className="text-sm text-slate-500">{[locInfo.place.region, locInfo.place.country].filter(Boolean).join(", ")}</div>
                    <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
                      <Field label={t('places.properties', 'Properties')}>{locInfo.place.property_count}</Field>
                      <Field label={t('places.demand', 'Buyer / investor demand')}>{locInfo.place.demand_buyers} / {locInfo.place.demand_investors}</Field>
                    </div>
                    <Button onClick={() => navigate(`/places/${locInfo.place.id}`)} data-testid="open-linked-place" className="w-full mt-4 rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white">
                      {t('places.openProfile', 'Open Place Profile')}
                    </Button>
                  </div>
                </div>
              )}

              {locInfo?.place?.poi && (
                <div className="bg-white border border-slate-200 rounded-sm p-5 space-y-3">
                  <h4 className="font-serif text-lg">{t('places.transport', 'Transport & access')}</h4>
                  {["airports", "highways", "universities", "hospitals", "tourism", "commercial"].map((k) => (
                    locInfo.place.poi[k] ? (
                      <Field key={k} label={t(`places.${k}`, k)}>{locInfo.place.poi[k]}</Field>
                    ) : null
                  ))}
                  {!["airports", "highways", "universities", "hospitals", "tourism", "commercial"].some((k) => locInfo.place.poi[k]) && (
                    <p className="text-sm text-slate-400">—</p>
                  )}
                </div>
              )}

              {p.external_3d_url && (
                <Button variant="outline" onClick={() => setActive(0)} data-testid="location-view-3d" className="w-full rounded-sm">
                  <Cube size={16} className="mr-1.5" />{t('places.view3d', 'View 3D project')}
                </Button>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {edit && (
        <PropertyForm open={edit} onOpenChange={setEdit} meta={meta} initial={p} onSubmit={(payload) => update.mutate(payload)} />
      )}
    </div>
  );
}
